import { lazy, Suspense } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '../components/ui';
import { AppShell } from './AppShell';
import { Providers } from './Providers';
import { HomePage } from '../features/home/HomePage';
import { LibraryPage } from '../features/library/LibraryPage';
import { ErrorBoundary } from '../components/feedback/ErrorBoundary';
import { AuthRouteHandler } from './AuthRouteHandler';

const StatisticsPage = lazy(() => import('../features/statistics/StatisticsPage').then((module) => ({ default: module.StatisticsPage })));
const SettingsPage = lazy(() => import('../features/settings/SettingsPage').then((module) => ({ default: module.SettingsPage })));
const BookFormPage = lazy(() => import('../features/books/BookFormPage').then((module) => ({ default: module.BookFormPage })));
const BookDetailPage = lazy(() => import('../features/books/BookDetailPage').then((module) => ({ default: module.BookDetailPage })));
const SessionPage = lazy(() => import('../features/reading/SessionPage').then((module) => ({ default: module.SessionPage })));
const JournalPage = lazy(() => import('../features/books/JournalPage').then((module) => ({ default: module.JournalPage })));
const HistoryPage = lazy(() => import('../features/books/HistoryPage').then((module) => ({ default: module.HistoryPage })));
const ProfilePage = lazy(() => import('../features/profile/ProfilePage').then((module) => ({ default: module.ProfilePage })));
const SocialPage = lazy(() => import('../features/social/SocialPage').then((module) => ({ default: module.SocialPage })));
const HelpPage = lazy(() => import('../features/help/HelpPage').then((module) => ({ default: module.HelpPage })));
const AboutPage = lazy(() => import('../features/about/AboutPage').then((module) => ({ default: module.AboutPage })));
const ReaderLibraryPage = lazy(() => import('../features/reader/ReaderLibraryPage').then((module) => ({ default: module.ReaderLibraryPage })));
const ReaderPage = lazy(() => import('../features/reader/ReaderPage').then((module) => ({ default: module.ReaderPage })));

const UpdatePrompt = () => {
  const { needRefresh: [needRefresh, setNeedRefresh], offlineReady: [offlineReady, setOfflineReady], updateServiceWorker } = useRegisterSW();
  if (!needRefresh && !offlineReady) return null;
  return <div className="pwa-prompt" role="status"><div><strong>{needRefresh ? 'Nova versão disponível' : 'LEITOUR pronta para ficar offline'}</strong><span>{needRefresh ? 'Atualize quando for um bom momento.' : 'Seus registros essenciais funcionam sem internet.'}</span></div>{needRefresh ? <Button onClick={() => updateServiceWorker(true)}>Atualizar</Button> : <Button variant="secondary" onClick={() => setOfflineReady(false)}>Entendi</Button>}<button aria-label="Fechar" onClick={() => { setNeedRefresh(false); setOfflineReady(false); }}>×</button></div>;
};

export const App = () => (
  <ErrorBoundary>
    <Providers>
      <HashRouter>
      <AuthRouteHandler />
      <UpdatePrompt />
      <Suspense fallback={<div className="page loading-page"><span className="loading-orb" />Abrindo sua leitura…</div>}>
        <Routes>
          <Route path="reader/:bookId" element={<ReaderPage />} />
          <Route element={<AppShell />}>
            <Route index element={<HomePage />} />
            <Route path="library" element={<LibraryPage />} />
            <Route path="books/new" element={<BookFormPage />} />
            <Route path="books/:id" element={<BookDetailPage />} />
            <Route path="books/:id/edit" element={<BookFormPage />} />
            <Route path="books/:id/notes" element={<JournalPage type="notes" />} />
            <Route path="books/:id/quotes" element={<JournalPage type="quotes" />} />
            <Route path="books/:id/history" element={<HistoryPage />} />
            <Route path="session/:sessionId" element={<SessionPage />} />
            <Route path="statistics" element={<StatisticsPage />} />
            <Route path="reader" element={<ReaderLibraryPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="social" element={<SocialPage />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="*" element={<HomePage />} />
          </Route>
        </Routes>
      </Suspense>
      </HashRouter>
    </Providers>
  </ErrorBoundary>
);
